import gqlClient from "./api/graphql.client";
import { UPDATE_MANY_WIKI_AUTHOR_SNAPSHOT } from "./wiki/wiki.query";
import { UPDATE_MANY_NEWS_AUTHOR_SNAPSHOT } from "./api/news/news.query";
import { UPDATE_MANY_FEATURE_REQUEST_AUTHOR_SNAPSHOT } from "./feature-request/feature-request.query";
import { UPDATE_MANY_GROUP_MEMBERSHIP_SNAPSHOT } from "./api/groups/groups.query";
import {
  UPDATE_MANY_COMMENT_AUTHOR_SNAPSHOT,
  UPDATE_MANY_POST_AUTHOR_SNAPSHOT,
  UPDATE_MANY_REACTION_ACTOR,
} from "./api/posts/posts.query";

export type NameSnapshot = {
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl: string | null;
};

export type SnapshotSyncStepResult = {
  entity: string;
  impacted: number;
  ok: boolean;
  error?: unknown;
};

/**
 * Refreshes every denormalized `authorSnapshot`/`groupUserSnapshot`/`userSnapshot`/
 * reactor copy of a user's name (and avatar) after a profile edit changes them.
 *
 * These snapshots exist so post/comment/article/reaction rows don't
 * need a join back to the user at read time — see `NotificationRow`'s comment on
 * the same trade-off for the notification payload. The cost is exactly this: a
 * rename leaves every previously-written row showing the old name until
 * something writes the new one over it. Without this sync a rename is
 * permanently invisible on old posts/comments. (Leaderboard names are derived on
 * the server from these reaction/comment snapshots, so they follow automatically.)
 *
 * Deliberately one `updateMany<Schema>` call per collection instead of paging
 * through the user's rows and updating each `ItemId` individually — the gateway
 * supports `updateMany<Schema>(where, input)` as a first-class bulk mutation
 * (mirrors `deleteMany<Schema>`, already used the same way by
 * `purgeUserData` for account deletion), so one call per collection updates an
 * arbitrary number of rows in a single request.
 *
 * Each `input.authorSnapshot`/`groupUserSnapshot` here is sent as a **complete**
 * nested object (all four fields), not just `firstName`/`lastName`. This
 * codebase's other nested-object updates (e.g. `UPDATE_POST_REACTION_COUNT_MUTATION`
 * always sending the full `reactionCount`) always replace the whole nested value
 * rather than relying on the gateway to merge individual subfields — sending a
 * partial object risks silently blanking `email`/`profileImageUrl` on every
 * matched row. Passing the complete, currently-correct value for every field is
 * safe under either a merge or a replace semantics, so this does not depend on
 * which one the gateway actually implements.
 *
 * Not covered: `mentionedUsers` entries embedded inside individual posts/comments.
 * Those are elements of an array field, not a `where`-filterable top-level column,
 * so a generic bulk mutation can't target them — fixing that would need a
 * per-record read of which rows mention this user, which is exactly the
 * one-by-one cost this function exists to avoid for the common case (authorship).
 *
 * Mirrors `purgeUserData`'s shape: independent per-collection steps, each
 * caught so one failing collection doesn't stop the rest or fail the caller —
 * a rename must never be blocked by a snapshot sync failure on an unrelated
 * feature.
 */
export const syncUserNameSnapshots = async (
  userId: string,
  snapshot: NameSnapshot,
): Promise<SnapshotSyncStepResult[]> => {
  const client = await gqlClient();
  const results: SnapshotSyncStepResult[] = [];

  const vars = {
    firstName: snapshot.firstName,
    lastName: snapshot.lastName,
    email: snapshot.email,
    profileImageUrl: snapshot.profileImageUrl,
  };

  const steps: { entity: string; run: () => Promise<unknown> }[] = [
    {
      entity: "posts",
      run: () =>
        client.request(UPDATE_MANY_POST_AUTHOR_SNAPSHOT, {
          userId,
          ...vars,
        }),
    },
    {
      entity: "comments",
      run: () =>
        client.request(UPDATE_MANY_COMMENT_AUTHOR_SNAPSHOT, {
          authorId: userId,
          ...vars,
        }),
    },
    {
      entity: "reactions",
      run: () =>
        client.request(UPDATE_MANY_REACTION_ACTOR, {
          userId,
          reactorName: `${snapshot.firstName} ${snapshot.lastName}`.trim(),
          reactorProfileImageUrl: snapshot.profileImageUrl,
        }),
    },
    {
      entity: "wiki",
      run: () =>
        client.request(UPDATE_MANY_WIKI_AUTHOR_SNAPSHOT, {
          createdBy: userId,
          ...vars,
        }),
    },
    {
      entity: "news",
      run: () =>
        client.request(UPDATE_MANY_NEWS_AUTHOR_SNAPSHOT, {
          createdBy: userId,
          ...vars,
        }),
    },
    {
      entity: "featureRequests",
      run: () =>
        client.request(UPDATE_MANY_FEATURE_REQUEST_AUTHOR_SNAPSHOT, {
          createdBy: userId,
          ...vars,
        }),
    },
    {
      entity: "groupMemberships",
      run: () =>
        client.request(UPDATE_MANY_GROUP_MEMBERSHIP_SNAPSHOT, {
          userId,
          ...vars,
        }),
    },
  ];

  for (const step of steps) {
    try {
      const res = (await step.run()) as Record<string, any> | undefined;
      const payload = res ? Object.values(res)[0] : undefined;
      const impacted = (payload as any)?.totalImpactedData ?? 0;
      results.push({ entity: step.entity, impacted, ok: true });
    } catch (error) {
      console.error(
        `syncUserNameSnapshots: failed to refresh ${step.entity}`,
        error,
      );
      results.push({ entity: step.entity, impacted: 0, ok: false, error });
    }
  }

  return results;
};
